import { BusinessCenter } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { Dialog } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  addEconomicActivityToBlacklist,
  deleteEconomicActivityFromBlacklist,
  getEconomicActivitiesBlacklist,
} from 'lib/api/economicActivitiesBlacklist';
import { EconomicActivitiesBlacklist } from 'lib/api/economicActivitiesBlacklist/types';
import {
  ECONOMIC_ACTIVITIES,
  EconomicActivity,
} from 'lib/consts/economicActivities';
import CustomButton from 'lib/designSystem/Button';
import EnhancedTable from 'lib/designSystem/Table';
import Title from 'lib/designSystem/Title';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

const HEADERS = [
  { key: 'code', label: 'Código' },
  { key: 'activity', label: 'Actividad' },
];

const EconomicActivities = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data } = getEconomicActivitiesBlacklist(dispatch);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [code, setCode] = useState('');

  const activitiesOptions = [...ECONOMIC_ACTIVITIES].sort((a, b) =>
    a.activity.localeCompare(b.activity),
  );

  const tableData = data?.map((item: EconomicActivitiesBlacklist) => {
    const activity = ECONOMIC_ACTIVITIES.find(
      (act: EconomicActivity) => act.code === item.code,
    );
    return {
      id: item.id,
      code: item.code,
      activity: activity?.activity || '',
    };
  });

  const handleAddEconomicActivity = async () => {
    try {
      if (!code || code === '') {
        showAlert(dispatch, 'error', 'Selecciona una actividad económica');
        return;
      }
      setLoading(true);
      await addEconomicActivityToBlacklist(dispatch, code);
      setOpenDialog(false);
      setCode('');
      await queryClient.invalidateQueries({
        queryKey: ['economicActivitiesBlacklist'],
      });
      showAlert(dispatch, 'success', 'Actividad económica agregada');
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  const handleDeleteEconomicActivity = async (id: number) => {
    try {
      if (!id) {
        showAlert(dispatch, 'error', 'Selecciona una actividad económica');
        return;
      }
      await deleteEconomicActivityFromBlacklist(dispatch, id);
      await queryClient.invalidateQueries({
        queryKey: ['economicActivitiesBlacklist'],
      });
      showAlert(dispatch, 'success', 'Actividad económica eliminada');
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  const actions = [
    {
      action: handleDeleteEconomicActivity,
      actionLabel: 'Eliminar',
    },
  ];

  return (
    <>
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Title
            title="Actividades Económicas"
            icon={<BusinessCenter color="primary" />}
            color="primary"
          />
          <CustomButton onClick={() => setOpenDialog(true)} color="primary">
            Agregar
          </CustomButton>
        </Stack>
        <EnhancedTable
          data={tableData || []}
          dataHeaders={HEADERS}
          exportToFile="xlsx"
          actions={actions}
        />
      </Box>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          onClose={() => setOpenDialog(false)}
          title="Agregar actividad económica"
          icon={<BusinessCenter color="primary" />}
          color="primary"
        />
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel id="economic-activity-select-label">
              Actividad económica
            </InputLabel>
            <Select
              labelId="economic-activity-select-label"
              value={code}
              label="Actividad económica"
              onChange={(e) => setCode(e.target.value)}
            >
              {activitiesOptions.map((activity: EconomicActivity) => (
                <MenuItem key={activity.code} value={activity.code}>
                  {activity.code} - {activity.activity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setOpenDialog(false)}>
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleAddEconomicActivity}
            loading={loading}
          >
            Agregar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EconomicActivities;
